import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { ConfirmModal } from './Modals'

export default function QuoteSettings({ session, onClose }) {
  const [settings, setSettings] = useState({
    trading_name: '', logo_url: '', company_number: '', vat_number: '',
    vat_registered: false, vat_rate: 20,
    vat_note: 'VAT is not charged as this business is not VAT registered',
    phone: '', email: '', address: '',
    day_rate: 350, hours_per_day: 8,
    material_markup_pct: 20, labour_markup_pct: 0,
  })
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    const { data, error } = await supabase.from('settings').select('*').eq('user_id', session.user.id).single()
    if (data) setSettings(s => ({ ...s, ...data }))
    setLoading(false)
  }

  const uploadLogo = async (file) => {
    setUploadError('')
    // Validate type and size
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp']
    if (!allowed.includes(file.type)) {
      setUploadError('Please use PNG, JPG, SVG or WebP')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('Image must be under 2MB')
      return
    }
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `${session.user.id}/logo.${ext}`
    // Remove existing logo first
    await supabase.storage.from('logos').remove([`${session.user.id}/logo.png`, `${session.user.id}/logo.jpg`, `${session.user.id}/logo.jpeg`, `${session.user.id}/logo.svg`, `${session.user.id}/logo.webp`])
    const { error } = await supabase.storage.from('logos').upload(path, file, { upsert: true })
    if (error) { setUploadError('Upload failed: ' + error.message); setUploading(false); return }
    const { data: { publicUrl } } = supabase.storage.from('logos').getPublicUrl(path)
    // Bust cache with timestamp
    const url = publicUrl + '?t=' + Date.now()
    set('logo_url', url)
    // Auto-save the logo_url immediately
    await supabase.from('settings').upsert({ user_id: session.user.id, logo_url: url }, { onConflict: 'user_id' })
    setUploading(false)
  }

  const deleteLogo = async () => {
    setConfirmModal({
      message: 'Remove logo?',
      confirmLabel: 'Remove',
      danger: true,
      onConfirm: async () => {
        const userId = session.user.id
        await supabase.storage.from('logos').remove([`${userId}/logo.png`, `${userId}/logo.jpg`, `${userId}/logo.jpeg`, `${userId}/logo.svg`, `${userId}/logo.webp`])
        set('logo_url', '')
        await supabase.from('settings').upsert({ user_id: userId, logo_url: '' }, { onConflict: 'user_id' })
      }
    })
  }

  const deleteAccount = async () => {
    if (!deleteConfirm) { setDeleteConfirm(true); return }
    setDeleting(true)
    const userId = session.user.id
    try {
      // Delete all user data in order (child tables first)
      const projectsRes = await supabase.from('projects').select('id').eq('user_id', userId)
      const projectIds = (projectsRes.data || []).map(p => p.id)

      if (projectIds.length > 0) {
        await supabase.from('line_items').delete().in('project_id', projectIds)
        await supabase.from('labour_lines').delete().in('project_id', projectIds)
        await supabase.from('time_tasks').delete().in('project_id', projectIds)
        await supabase.from('project_sections').delete().in('project_id', projectIds)
      }
      await supabase.from('projects').delete().eq('user_id', userId)
      await supabase.from('item_catalogue').delete().eq('user_id', userId)
      await supabase.from('task_catalogue').delete().eq('user_id', userId)
      await supabase.from('settings').delete().eq('user_id', userId)

      // Delete logo from storage
      const extensions = ['png', 'jpg', 'jpeg', 'svg', 'webp']
      await supabase.storage.from('logos').remove(extensions.map(e => `${userId}/logo.${e}`))

      // Sign out and delete auth account
      await supabase.auth.signOut()
      // Note: full auth user deletion requires admin API — sign out is sufficient for access
    } catch (e) {
      console.error('Delete account error:', e)
      setDeleting(false)
      setDeleteConfirm(false)
    }
  }

  const save = async () => {
    // Strip any stale id/created_at from state, build clean payload
    const { id: _id, created_at: _c, updated_at: _u, ...fields } = settings
    const payload = { ...fields, user_id: session.user.id }

    const { error } = await supabase
      .from('settings')
      .upsert(payload, { onConflict: 'user_id' })

    if (error) {
      console.error('Settings save error:', error.message)
      alert('Save failed: ' + error.message)
      return
    }
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const set = (field, value) => setSettings(s => ({ ...s, [field]: value }))

  if (loading) return (
    <div className="ak-overlay">
      <div className="ak-modal" style={{ padding: '2rem', textAlign: 'center' }}>
        <span className="t-label">Loading…</span>
      </div>
    </div>
  )

  return (
    <div className="ak-overlay">
      <ConfirmModal config={confirmModal} onClose={() => setConfirmModal(null)} />
      <div className="ak-modal" style={{ maxWidth: '520px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div className="ak-modal-head">
          <span className="ak-modal-title">Account settings</span>
          <button className="ak-btn ak-btn--ghost ak-btn--sm" onClick={onClose}>Close ×</button>
        </div>

        {/* Scrollable body */}
        <div className="ak-modal-body" style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Logo */}
          <section>
            <div className="t-label" style={{ marginBottom: '0.4rem', paddingBottom: '0.25rem', borderBottom: '1px solid var(--line)' }}>Logo</div>
            <p style={{ fontSize: '11px', color: 'var(--ink-3)', marginBottom: '0.6rem', lineHeight: 1.5 }}>
              Shown on quotes in place of your trading name. PNG, JPG, SVG or WebP · max 2MB · recommended max width 400px.
            </p>
            <div style={{ padding: '0.25rem 0' }}>
              {settings.logo_url ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <img
                    src={settings.logo_url}
                    alt="Logo"
                    style={{ maxHeight: '64px', maxWidth: '200px', objectFit: 'contain', border: '1px solid var(--line-2)', borderRadius: 'var(--radius)', padding: '6px', background: 'var(--surface)' }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <label className="ak-btn ak-btn--ghost ak-btn--sm" style={{ cursor: 'pointer' }}>
                      {uploading ? 'Uploading…' : 'Replace'}
                      <input type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                        style={{ display: 'none' }}
                        onChange={e => e.target.files[0] && uploadLogo(e.target.files[0])} />
                    </label>
                    <button
                      style={{ background: 'none', border: 'none', fontSize: '11px', fontFamily: 'var(--mono)', color: 'var(--ink-3)', cursor: 'pointer', padding: '4px 0', textAlign: 'left', textDecoration: 'underline' }}
                      onClick={deleteLogo}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  style={{
                    display: 'inline-block',
                    border: '1.5px dashed var(--green)',
                    borderRadius: 'var(--radius)',
                    padding: '0.5rem 1rem',
                    fontFamily: 'var(--mono)',
                    fontSize: '10.5px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    color: 'var(--green)',
                    cursor: 'pointer',
                    background: 'var(--green-soft)',
                  }}
                >
                  {uploading ? 'Uploading…' : '+ Upload logo'}
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    style={{ display: 'none' }}
                    onChange={e => e.target.files[0] && uploadLogo(e.target.files[0])} />
                </label>
              )}
              {uploadError && (
                <div style={{ marginTop: '0.4rem', fontFamily: 'var(--mono)', fontSize: '11px', color: '#c0392b' }}>
                  {uploadError}
                </div>
              )}
            </div>
          </section>

          {/* Your business */}
          <section>
            <div className="t-label" style={{ marginBottom: '0.4rem', paddingBottom: '0.25rem', borderBottom: '1px solid var(--line)' }}>Your business</div>
            <Field label="Trading name" value={settings.trading_name} onChange={v => set('trading_name', v)} />
            <Field label="Email" value={settings.email} onChange={v => set('email', v)} type="email" />
            <Field label="Phone" value={settings.phone} onChange={v => set('phone', v)} />
            <FieldArea label="Address" value={settings.address} onChange={v => set('address', v)} />
            <Field label="Company number" value={settings.company_number} onChange={v => set('company_number', v)} placeholder="Optional" />
          </section>

          {/* VAT */}
          <section>
            <div className="t-label" style={{ marginBottom: '0.4rem', paddingBottom: '0.25rem', borderBottom: '1px solid var(--line)' }}>VAT</div>
            <SettingsRow label="VAT registered">
              <div
                className={`ak-toggle${settings.vat_registered ? ' on' : ''}`}
                onClick={() => set('vat_registered', !settings.vat_registered)}
              >
                <div className="knob" />
              </div>
            </SettingsRow>
            {settings.vat_registered ? (
              <>
                <Field label="VAT number" value={settings.vat_number} onChange={v => set('vat_number', v)} />
                <SettingsRow label="VAT rate">
                  <PctInput
                    value={settings.vat_rate ?? 20}
                    onChange={v => set('vat_rate', v)}
                    min={0} max={30} step={1} suffix="%" parse={parseFloat}
                  />
                </SettingsRow>
              </>
            ) : (
              <FieldArea label="VAT note on quotes" value={settings.vat_note} onChange={v => set('vat_note', v)} rows={2} />
            )}
          </section>

          {/* Labour defaults */}
          <section>
            <div className="t-label" style={{ marginBottom: '0.4rem', paddingBottom: '0.25rem', borderBottom: '1px solid var(--line)' }}>Labour defaults</div>
            <p style={{ fontSize: '11px', color: 'var(--ink-3)', marginBottom: '0.5rem' }}>Applied to new projects. Can be overridden per project.</p>
            <SettingsRow label="Day rate">
              <PctInput
                value={settings.day_rate ?? 350}
                onChange={v => set('day_rate', v)}
                min={0} prefix="£" parse={v => parseFloat(v) || 350}
              />
            </SettingsRow>
            <SettingsRow label="Hours per day">
              <PctInput
                value={settings.hours_per_day ?? 8}
                onChange={v => set('hours_per_day', v)}
                min={1} max={24} suffix="hrs" parse={v => parseInt(v) || 8}
              />
            </SettingsRow>
          </section>

          {/* Default mark-up */}
          <section>
            <div className="t-label" style={{ marginBottom: '0.4rem', paddingBottom: '0.25rem', borderBottom: '1px solid var(--line)' }}>Default mark-up</div>
            <p style={{ fontSize: '11px', color: 'var(--ink-3)', marginBottom: '0.5rem' }}>Applied to all quotes. Can be overridden per quote.</p>
            <SettingsRow label="Materials">
              <PctInput
                value={settings.material_markup_pct}
                onChange={v => set('material_markup_pct', v)}
                min={0} max={100} suffix="%" parse={v => parseFloat(v) || 0}
              />
            </SettingsRow>
            <SettingsRow label="Labour">
              <PctInput
                value={settings.labour_markup_pct}
                onChange={v => set('labour_markup_pct', v)}
                min={0} max={100} suffix="%" parse={v => parseFloat(v) || 0}
              />
            </SettingsRow>
          </section>

          {/* Danger zone */}
          <section style={{ marginTop: '0.5rem', paddingTop: '1.25rem', borderTop: '2px solid var(--line-2)' }}>
            <div className="t-label" style={{ marginBottom: '0.5rem', color: '#dc2626' }}>Danger zone</div>
            <p style={{ fontSize: '12px', color: 'var(--ink-2)', marginBottom: '0.75rem', lineHeight: 1.5 }}>
              Permanently delete your account and all associated data — projects, settings, catalogue items, and logo. This cannot be undone.
            </p>
            {deleteConfirm ? (
              <div style={{ background: '#fff5f5', border: '1.5px solid #fca5a5', borderRadius: 'var(--radius)', padding: '0.85rem 1rem' }}>
                <p style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: '#991b1b', marginBottom: '0.75rem', lineHeight: 1.6 }}>
                  Are you sure? All your projects, settings, and data will be permanently deleted. There is no undo.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    style={{ padding: '0.45rem 0.85rem', background: '#dc2626', border: 'none', borderRadius: 'var(--radius-ctl)', color: 'white', fontFamily: 'var(--mono)', fontSize: '11px', cursor: 'pointer' }}
                    onClick={deleteAccount}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting…' : 'Yes, delete everything'}
                  </button>
                  <button
                    style={{ padding: '0.45rem 0.85rem', background: 'none', border: '1px solid var(--line-2)', borderRadius: 'var(--radius-ctl)', fontFamily: 'var(--mono)', fontSize: '11px', cursor: 'pointer' }}
                    onClick={() => setDeleteConfirm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                style={{ padding: '0.45rem 0.85rem', background: 'none', border: '1.5px solid #fca5a5', borderRadius: 'var(--radius-ctl)', color: '#dc2626', fontFamily: 'var(--mono)', fontSize: '11px', cursor: 'pointer' }}
                onClick={deleteAccount}
              >
                Delete my account
              </button>
            )}
          </section>

        </div>

        {/* Footer */}
        <div className="ak-modal-foot">
          <button className="ak-btn ak-btn--primary" onClick={save}>
            {saved ? '✓ Saved' : 'Save settings'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ helpers */

function SettingsRow({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--ink-2)', width: '130px', flexShrink: 0 }}>{label}</span>
      {children}
    </div>
  )
}

function Field({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--ink-2)', width: '130px', flexShrink: 0 }}>{label}</span>
      <input className="ak-input" style={{ flex: 1 }} type={type} value={value || ''} placeholder={placeholder}
        onChange={e => onChange(e.target.value)} />
    </div>
  )
}

function FieldArea({ label, value, onChange, rows = 3 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.4rem 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--ink-2)', width: '130px', flexShrink: 0, paddingTop: '0.3rem' }}>{label}</span>
      <textarea
        className="ak-input"
        style={{ flex: 1, resize: 'vertical' }}
        rows={rows}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  )
}

function PctInput({ value, onChange, min, max, step, prefix, suffix, parse }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
      {prefix && <span style={{ fontFamily: 'var(--mono)', fontSize: '12px', color: 'var(--ink-3)', padding: '0 4px' }}>{prefix}</span>}
      <input
        className="ak-input ak-input--num"
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(parse(e.target.value))}
      />
      {suffix && <span style={{ fontFamily: 'var(--mono)', fontSize: '12px', color: 'var(--ink-3)', padding: '0 4px' }}>{suffix}</span>}
    </div>
  )
}
